import { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ShoppingCart, CheckCircle, Spinner, User, Buildings, MagnifyingGlass, Plus, Minus, Trash, X } from "@phosphor-icons/react";
import { useCart, formatMoney } from "@/lib/cart";
import { cartCheckout, fetchCustomers, previewCustomerTier } from "@/lib/api";
import { useT } from "@/lib/i18n";
import ShareLinkBox from "@/components/ShareLinkBox";

export default function CheckoutPage({ user, onLogout }) {
  const navigate = useNavigate();
  const t = useT();
  const { items, subtotal, currency, clear, updateQty, removeItem } = useCart();
  const isAdmin = Boolean(user?.is_admin);
  const isCustomer = user?.role === "customer";
  const isGuest = !user || (!isAdmin && !isCustomer);

  const [mode, setMode] = useState(isAdmin ? "existing" : "new"); // "existing" | "new"
  const [customerQuery, setCustomerQuery] = useState("");
  const [customerResults, setCustomerResults] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [searchingCust, setSearchingCust] = useState(false);

  const [billing, setBilling] = useState({
    company_name: "",
    contact_name: user?.name || "",
    email: user?.email && !user.email.includes("local.guest") ? user.email : "",
    phone: "",
    address: "",
  });

  const [shipping, setShipping] = useState(0);
  const [salesTax, setSalesTax] = useState(0);
  const [startUp, setStartUp] = useState(0);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [submittedSnapshot, setSubmittedSnapshot] = useState(null);

  // Save as Preliminary + CC email chips
  const [saveAsPreliminary, setSaveAsPreliminary] = useState(false);
  const [ccEmails, setCcEmails] = useState([]);
  const [ccInput, setCcInput] = useState("");
  const [ccError, setCcError] = useState("");

  // Customer_Aux tier preview (admin-only) — shows "D-series 15% OFF · Net-30"
  // banner when a tier exists. Null = not loaded yet or no customer selected.
  const [tier, setTier] = useState(null);
  useEffect(() => {
    if (!isAdmin || !selectedCustomer?.CustomerID) { setTier(null); return; }
    let cancelled = false;
    previewCustomerTier(selectedCustomer.CustomerID)
      .then(t => { if (!cancelled) setTier(t); })
      .catch(() => { if (!cancelled) setTier(null); });
    return () => { cancelled = true; };
  }, [isAdmin, selectedCustomer?.CustomerID]);

  const total = useMemo(
    () => subtotal + Number(shipping || 0) + Number(salesTax || 0) + Number(startUp || 0),
    [subtotal, shipping, salesTax, startUp]
  );

  // Final recipient preview — merges buyer email + user email + cc chips (deduped).
  const sanitizedUserEmail = user?.email && !user.email.includes("@local.guest") ? user.email : "";
  const previewRecipients = useMemo(() => {
    const out = [];
    const seen = new Set();
    const push = (e) => {
      const v = (e || "").trim().toLowerCase();
      if (v && v.includes("@") && !seen.has(v)) { seen.add(v); out.push(v); }
    };
    push(billing.email);
    push(sanitizedUserEmail);
    ccEmails.forEach(push);
    return out;
  }, [billing.email, sanitizedUserEmail, ccEmails]);

  const addCcChip = () => {
    const e = ccInput.trim().toLowerCase();
    if (!e) return;
    if (!e.includes("@") || !/.+@.+\..+/.test(e)) {
      setCcError("Not a valid email");
      return;
    }
    if (ccEmails.includes(e)) {
      setCcError("Already added");
      return;
    }
    setCcEmails([...ccEmails, e]);
    setCcInput("");
    setCcError("");
  };
  const removeCcChip = (idx) => setCcEmails(ccEmails.filter((_, i) => i !== idx));

  // Admin customer search (debounced)
  useEffect(() => {
    if (!isAdmin || mode !== "existing") return;
    const q = customerQuery.trim();
    if (q.length < 2) { setCustomerResults([]); return; }
    const t = setTimeout(async () => {
      setSearchingCust(true);
      try {
        const data = await fetchCustomers({ search: q, per_page: 8, page: 1 });
        setCustomerResults(data?.items || []);
      } catch {
        setCustomerResults([]);
      } finally {
        setSearchingCust(false);
      }
    }, 280);
    return () => clearTimeout(t);
  }, [customerQuery, isAdmin, mode]);

  const handleSubmit = useCallback(async () => {
    setError("");
    if (!items.length) { setError("Your cart is empty."); return; }

    // Build request payload based on role/mode
    const payload = {
      items: items.map((it) => ({
        product_code: it.product_code,
        part_id: it.part_id || null,
        product_name: it.product_name,
        quantity: Number(it.quantity) || 1,
        price: Number(it.price) || 0,
        currency: it.currency || currency,
        discount: Math.max(0, Math.min(1, Number(it.discount) || 0)),
        kit_label: it.kit_label || "",
      })),
      shipping: Number(shipping) || 0,
      sales_tax: Number(salesTax) || 0,
      start_up_charge: Number(startUp) || 0,
      notes: notes.trim(),
      save_as_preliminary: saveAsPreliminary,
      copy_to_emails: ccEmails,
    };

    if (isCustomer) {
      // Backend auto-resolves from session — still include billing for fallback
      payload.billing = { ...billing };
    } else if (isAdmin && mode === "existing") {
      if (!selectedCustomer) { setError("Please select a customer."); return; }
      payload.customer_id_code = selectedCustomer.CustomerID;
    } else {
      // Guest or admin-creating-new
      if (!billing.company_name.trim() && !billing.email.trim()) {
        setError("Please enter at least a Company Name or Email.");
        return;
      }
      payload.billing = { ...billing };
    }

    setSubmitting(true);
    try {
      const res = await cartCheckout(payload);
      // Snapshot so the success screen can show the full details even after the cart is cleared.
      setSubmittedSnapshot({
        items: [...items],
        billing: { ...billing },
        selectedCustomer: selectedCustomer,
        notes: notes.trim(),
        shipping: Number(shipping) || 0,
        salesTax: Number(salesTax) || 0,
        startUp: Number(startUp) || 0,
        currency,
      });
      setResult(res);
      clear();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Checkout failed");
    } finally {
      setSubmitting(false);
    }
  }, [items, currency, shipping, salesTax, startUp, notes, billing, mode, selectedCustomer, isAdmin, isCustomer, clear, saveAsPreliminary, ccEmails]);

  // Success screen
  if (result) {
    const snap = submittedSnapshot || {};
    const snapItems = snap.items || [];
    const snapBilling = snap.billing || {};
    const snapCur = snap.currency || result.currency || "USD";
    const snapSubtotal = snapItems.reduce((s, it) => {
      const disc = Math.max(0, Math.min(1, Number(it.discount) || 0));
      return s + (Number(it.quantity) || 1) * (Number(it.price) || 0) * (1 - disc);
    }, 0);

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-emerald-50 py-10 px-4">
        <div data-testid="checkout-success-card" className="max-w-3xl mx-auto bg-white border border-slate-200 rounded-lg shadow-xl overflow-hidden">
          {/* Header */}
          <div className={`px-6 py-6 border-b border-slate-200 text-center ${result.is_preliminary ? "bg-gradient-to-r from-amber-50 to-orange-50" : "bg-gradient-to-r from-emerald-50 to-blue-50"}`}>
            <CheckCircle size={48} weight="fill" className={result.is_preliminary ? "text-amber-500 mx-auto mb-2" : "text-emerald-500 mx-auto mb-2"} />
            <h1 className="text-2xl font-black font-['Chivo'] text-slate-900">
              {result.is_preliminary ? "Preliminary draft saved!" : "Quote created!"}
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Reference: <span data-testid="success-quote-id" className="font-mono font-bold text-slate-900">{result.quote_id}</span>
              {result.is_preliminary && (
                <span data-testid="success-preliminary-badge" className="ml-2 inline-block text-[10px] font-bold uppercase tracking-wider text-amber-800 bg-amber-100 border border-amber-300 px-1.5 py-0.5 rounded-sm">Preliminary</span>
              )}
            </p>
            {result.is_new_lead && (
              <p className="text-[11px] text-emerald-700 font-semibold mt-2 inline-block bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-sm">
                {t("checkout.new_customer_created", "✓ A new customer record was created for this order")}
              </p>
            )}
            {Array.isArray(result.copies_sent) && result.copies_sent.length > 0 && (
              <div data-testid="success-copies-sent" className="mt-3 text-[11px] text-slate-600">
                <p className="font-bold uppercase tracking-wider text-[10px] text-slate-400 mb-1">
                  {result.is_preliminary ? "Draft emailed to" : "Confirmation emailed to"}
                </p>
                <div className="flex flex-wrap justify-center gap-1.5">
                  {result.copies_sent.map((em) => (
                    <span key={em} className="inline-block bg-white border border-slate-300 text-slate-700 px-2 py-0.5 rounded-sm font-mono text-[10px]">{em}</span>
                  ))}
                </div>
              </div>
            )}
            {(!result.copies_sent || result.copies_sent.length === 0) && (
              <p className="text-[11px] text-slate-500 mt-3 italic">
                {t("checkout.no_buyer_email", "No buyer email on file — only the sales team was notified. They'll reach out via phone / address on the quote.")}
              </p>
            )}
          </div>

          {/* Magic-link for sharing / forwarding */}
          {result.share_token && (
            <section className="px-6 pt-5 pb-2">
              <ShareLinkBox
                link={`${window.location.origin}/quote/${encodeURIComponent(result.quote_id)}?t=${encodeURIComponent(result.share_token)}`}
                label={t("checkout.share_label", "Share this quote with anyone")}
                description={t("checkout.share_description", "Forward this link to a colleague or decision-maker — they can review the full quote without signing in. Link is valid for 90 days.")}
                testidPrefix="success-share-link"
              />
            </section>
          )}

          {/* Buyer details */}
          <section className="px-6 py-5 border-b border-slate-100">
            <h2 className="text-xs font-black uppercase tracking-wider text-slate-500 mb-3">
              <Buildings size={12} className="inline mr-1 mb-0.5" />Buyer Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-6 text-sm">
              <SuccessField label={t("checkout.customer_id", "Customer ID")} value={result.customer_id_code} mono />
              <SuccessField label="Company" value={snapBilling.company_name || snap.selectedCustomer?.["Company Name"] || "—"} />
              <SuccessField label="Contact" value={snapBilling.contact_name || "—"} />
              <SuccessField label="Email" value={snapBilling.email || snap.selectedCustomer?.["Email(1)"] || "—"} />
              <SuccessField label="Phone" value={snapBilling.phone || "—"} />
              <SuccessField label={t("checkout.prepared_by", "Prepared by")} value={result.prepared_by || "—"} mono />
              {snapBilling.address && (
                <div className="md:col-span-2">
                  <SuccessField label="Ship-to" value={snapBilling.address} />
                </div>
              )}
              {snap.notes && (
                <div className="md:col-span-2">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">{t("checkout.buyer_note", "Buyer note")}</p>
                  <p className="text-sm text-slate-700 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 italic">{snap.notes}</p>
                </div>
              )}
            </div>
          </section>

          {/* Items */}
          <section className="px-6 py-5 border-b border-slate-100">
            <h2 className="text-xs font-black uppercase tracking-wider text-slate-500 mb-3">
              {t("checkout.items_purchased", "Items Purchased")} ({result.line_count})
            </h2>
            <div className="border border-slate-200 rounded-sm overflow-hidden" data-testid="success-items-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    <th className="px-3 py-2 text-left">{t("table.product", "Product")}</th>
                    <th className="px-3 py-2 text-right w-[60px]">{t("table.qty", "Qty")}</th>
                    <th className="px-3 py-2 text-right w-[110px]">{t("table.price", "Price")}</th>
                    <th className="px-3 py-2 text-right w-[120px]">{t("checkout.line_total", "Line total")}</th>
                  </tr>
                </thead>
                <tbody>
                  {snapItems.map((it) => {
                    const q = Number(it.quantity) || 1;
                    const price = Number(it.price) || 0;
                    const disc = Math.max(0, Math.min(1, Number(it.discount) || 0));
                    const line = q * price * (1 - disc);
                    const kitLabelMap = { break_in: "Break-In Kit", service_3000: "3,000-hr Kit" };
                    return (
                      <tr key={it.product_code + "|" + (it.kit_label || "")} className="border-b border-slate-100 last:border-b-0">
                        <td className="px-3 py-2">
                          <p className="font-semibold text-slate-900">{it.product_name}</p>
                          <p className="text-[10px] font-mono text-slate-400">{it.product_code}</p>
                          {disc > 0 && (
                            <span className="inline-flex items-center gap-1 mt-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-emerald-100 text-emerald-800 border border-emerald-200">
                              {kitLabelMap[it.kit_label] || "Kit"} · −{Math.round(disc * 100)}%
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-slate-700">{q}</td>
                        <td className="px-3 py-2 text-right font-mono text-slate-700">{formatMoney(price, snapCur)}</td>
                        <td className="px-3 py-2 text-right font-mono font-bold text-slate-900">{formatMoney(line, snapCur)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {/* Totals */}
          <section className="px-6 py-5 bg-slate-50">
            <div className="max-w-xs ml-auto space-y-1.5 text-sm">
              <SumRow label="Subtotal" value={formatMoney(snapSubtotal, snapCur)} />
              {Number(snap.shipping) > 0 && <SumRow label="Shipping" value={formatMoney(snap.shipping, snapCur)} />}
              {Number(snap.salesTax) > 0 && <SumRow label={t("checkout.sales_tax", "Sales tax")} value={formatMoney(snap.salesTax, snapCur)} />}
              {Number(snap.startUp) > 0 && <SumRow label="Start-up" value={formatMoney(snap.startUp, snapCur)} />}
              <div className="flex items-center justify-between pt-2 border-t border-slate-300 mt-2">
                <span className="text-xs font-black uppercase tracking-wider text-slate-700">Total</span>
                <span data-testid="success-total" className="text-xl font-black font-mono text-slate-900">{formatMoney(result.total, snapCur)}</span>
              </div>
            </div>
          </section>

          {/* Actions */}
          <section className="px-6 py-5 border-t border-slate-200 bg-white flex flex-wrap gap-2 justify-end">
            <button
              data-testid="success-view-quote"
              onClick={() => navigate(`/quote/${encodeURIComponent(result.quote_id)}`)}
              className="h-11 px-5 text-sm font-bold rounded-sm border border-blue-700 text-blue-700 hover:bg-blue-50 transition-colors"
            >
              {t("checkout.view_edit_quote", "View / edit quote →")}
            </button>
            <button
              data-testid="success-home"
              onClick={() => navigate("/")}
              className="h-11 px-5 text-sm font-semibold rounded-sm border border-slate-300 hover:bg-slate-50"
            >
              Home
            </button>
            <button
              data-testid="success-continue"
              onClick={() => navigate("/us-class")}
              className="h-11 px-5 text-sm font-bold rounded-sm text-white bg-blue-700 hover:bg-blue-800"
            >
              {t("checkout.keep_shopping", "Keep shopping")}
            </button>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 h-14 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500" title={t("action.back", "Back")}>
            <ArrowLeft size={18} weight="bold" />
          </button>
          <div className="flex items-center gap-2">
            <ShoppingCart size={20} weight="bold" className="text-blue-700" />
            <h1 className="text-lg font-black tracking-tighter font-['Chivo']">{t("checkout.title", "Checkout")}</h1>
          </div>
          <div className="ml-auto flex items-center gap-2 text-xs text-slate-500">
            <User size={14} weight="bold" />
            <span className="hidden md:inline max-w-[160px] truncate">{user?.email || t("auth.signin_guest", "Guest")}</span>
            {isAdmin && <span className="text-[9px] font-bold uppercase tracking-wider text-blue-600 bg-blue-50 border border-blue-200 px-1 py-0.5 rounded-sm">{t("help.role_admin", "Admin")}</span>}
            {isCustomer && <span className="text-[9px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-1 py-0.5 rounded-sm">{t("help.role_customer", "Customer")}</span>}
            {isGuest && <span className="text-[9px] font-bold uppercase tracking-wider text-slate-500 bg-slate-100 border border-slate-200 px-1 py-0.5 rounded-sm">{t("help.role_guest", "Guest")}</span>}
            {onLogout && user && (
              <button onClick={onLogout} className="text-xs text-slate-400 hover:text-slate-700 underline ml-2">{t("nav.signout", "Sign out")}</button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto p-4 md:p-8 grid md:grid-cols-[1fr_380px] gap-6">
        {/* Left: form */}
        <section className="space-y-5">
          {items.length === 0 ? (
            <div data-testid="checkout-empty" className="bg-white border border-slate-200 rounded-sm p-8 text-center">
              <p className="text-sm font-semibold text-slate-600 mb-1">{t("checkout.cart_empty", "Your cart is empty.")}</p>
              <p className="text-xs text-slate-400 mb-4">{t("checkout.add_items_from", "Add items from the price list before checking out.")}</p>
              <button onClick={() => navigate("/us-class")}
                className="h-10 px-4 text-sm font-bold rounded-sm text-white bg-blue-700 hover:bg-blue-800">
                {t("checkout.go_price_list", "Go to Price List")}
              </button>
            </div>
          ) : (
            <>
              {/* Customer section */}
              {isAdmin && (
                <div className="bg-white border border-slate-200 rounded-sm p-5">
                  <h2 className="text-sm font-black uppercase tracking-wider text-slate-700 mb-3">
                    <Buildings size={14} className="inline mr-1.5 mb-0.5" />Customer
                  </h2>
                  <div className="flex gap-1.5 mb-4" role="tablist">
                    <button
                      data-testid="checkout-mode-existing"
                      onClick={() => setMode("existing")}
                      className={`px-3 py-1.5 text-xs font-semibold rounded-sm border transition-colors ${
                        mode === "existing"
                          ? "bg-blue-700 text-white border-blue-700"
                          : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                      }`}
                    >
                      {t("checkout.existing_customer", "Existing customer")}
                    </button>
                    <button
                      data-testid="checkout-mode-new"
                      onClick={() => setMode("new")}
                      className={`px-3 py-1.5 text-xs font-semibold rounded-sm border transition-colors ${
                        mode === "new"
                          ? "bg-blue-700 text-white border-blue-700"
                          : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                      }`}
                    >
                      {t("checkout.new_customer", "New customer")}
                    </button>
                  </div>

                  {mode === "existing" ? (
                    <div className="relative">
                      <div className="relative">
                        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                          data-testid="checkout-customer-search"
                          value={customerQuery}
                          onChange={(e) => {
                            setCustomerQuery(e.target.value);
                            if (selectedCustomer) setSelectedCustomer(null);
                          }}
                          placeholder={t("checkout.search_customers_placeholder", "Search customers by company, email, or ID...")}
                          className="w-full h-10 pl-9 pr-3 text-sm border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                        />
                      </div>
                      {selectedCustomer ? (
                        <>
                          <div className="mt-2 p-3 border border-emerald-200 bg-emerald-50 rounded-sm flex items-start justify-between gap-2">
                            <div>
                              <p data-testid="checkout-selected-customer" className="text-sm font-bold text-slate-900">{selectedCustomer["Company Name"]}</p>
                              <p className="text-[11px] font-mono text-slate-500">{selectedCustomer.CustomerID}</p>
                              <p className="text-xs text-slate-500">{selectedCustomer["Email(1)"] || "—"}</p>
                            </div>
                            <button
                              data-testid="checkout-clear-customer"
                              onClick={() => setSelectedCustomer(null)}
                              className="text-xs text-slate-400 hover:text-slate-700 underline">Change</button>
                          </div>
                          <CustomerTierBanner tier={tier} t={t} />
                        </>
                      ) : customerQuery.trim().length >= 2 ? (
                        <div className="mt-2 border border-slate-200 rounded-sm max-h-64 overflow-auto bg-white">
                          {searchingCust ? (
                            <div className="p-3 text-xs text-slate-400">Searching…</div>
                          ) : customerResults.length === 0 ? (
                            <div className="p-3 text-xs text-slate-400">{t("checkout.no_matches", "No matches.")}</div>
                          ) : (
                            customerResults.map((c) => (
                              <button
                                key={c.CustomerID}
                                data-testid={`checkout-customer-${c.CustomerID}`}
                                onClick={() => { setSelectedCustomer(c); setCustomerQuery(""); setCustomerResults([]); }}
                                className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-slate-100 last:border-b-0"
                              >
                                <p className="text-sm font-semibold text-slate-900 truncate">{c["Company Name"]}</p>
                                <p className="text-[11px] text-slate-500 truncate">{c.CustomerID} · {c["Email(1)"] || "no email"}</p>
                              </button>
                            ))
                          )}
                        </div>
                      ) : (
                        <p className="mt-2 text-[11px] text-slate-400">{t("checkout.type_min_chars", "Type at least 2 characters to search.")}</p>
                      )}
                    </div>
                  ) : (
                    <BillingForm billing={billing} setBilling={setBilling} testPrefix="checkout-admin-new" />
                  )}
                </div>
              )}

              {/* Billing for guests & customers */}
              {!isAdmin && (
                <div className="bg-white border border-slate-200 rounded-sm p-5">
                  <h2 className="text-sm font-black uppercase tracking-wider text-slate-700 mb-3">
                    <Buildings size={14} className="inline mr-1.5 mb-0.5" />
                    {isCustomer ? "Ship-to / Billing" : "Your Details"}
                  </h2>
                  {isCustomer && (
                    <p className="text-[11px] text-slate-500 mb-3 italic">
                      {t("checkout.attached_to_account", "This quote will be attached to your account. Fill in any ship-to / contact info you'd like on the quote.")}
                    </p>
                  )}
                  <BillingForm billing={billing} setBilling={setBilling} testPrefix="checkout-guest" />
                </div>
              )}

              {/* Totals & charges */}
              <div className="bg-white border border-slate-200 rounded-sm p-5">
                <h2 className="text-sm font-black uppercase tracking-wider text-slate-700 mb-3">{t("checkout.notes_charges", "Notes & Charges")}</h2>
                <label className="block text-xs font-semibold text-slate-600 mb-1">{t("checkout.message_optional", "Message for our team (optional)")}</label>
                <textarea
                  data-testid="checkout-notes"
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={t("checkout.notes_placeholder", "Any special requirements, delivery windows, etc.")}
                  className="w-full text-sm border border-slate-300 rounded-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600"
                />
                {isAdmin && (
                  <div className="grid grid-cols-3 gap-3 mt-3">
                    <NumInput label="Shipping" value={shipping} onChange={setShipping} tid="checkout-shipping" />
                    <NumInput label={t("checkout.sales_tax_label", "Sales Tax")} value={salesTax} onChange={setSalesTax} tid="checkout-salestax" />
                    <NumInput label="Start-up" value={startUp} onChange={setStartUp} tid="checkout-startup" />
                  </div>
                )}
              </div>

              {/* Preliminary toggle + Email recipients */}
              <div className="bg-white border border-slate-200 rounded-sm p-5">
                <h2 className="text-sm font-black uppercase tracking-wider text-slate-700 mb-3">
                  {t("checkout.email_copy", "Email a copy of this quote")}
                </h2>

                <label data-testid="checkout-preliminary-toggle" className="flex items-start gap-3 p-3 border border-slate-200 rounded-sm cursor-pointer hover:bg-slate-50 mb-3">
                  <input
                    type="checkbox"
                    data-testid="checkout-preliminary-checkbox"
                    checked={saveAsPreliminary}
                    onChange={(e) => setSaveAsPreliminary(e.target.checked)}
                    className="mt-0.5 w-4 h-4 text-blue-700 border-slate-300 rounded-sm focus:ring-2 focus:ring-blue-500"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-bold text-slate-900">
                      {t("checkout.save_as", "Save as")} <span className="text-amber-700">Preliminary</span>
                    </p>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      {t("checkout.save_draft_hint", "Save a draft without locking it as a formal quote yet. Sales will still see it, but the email is tagged")} <span className="font-mono bg-amber-50 border border-amber-200 px-1 rounded-sm">[PRELIMINARY]</span>.
                    </p>
                  </div>
                </label>

                {/* Auto-included addresses */}
                <div className="mb-2">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">{t("checkout.will_be_sent_to", "Will be sent to")}</p>
                  <div className="flex flex-wrap gap-1.5 mb-2 min-h-[28px]">
                    {billing.email && (
                      <span data-testid="checkout-recipient-buyer" className="inline-flex items-center gap-1 text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-sm">
                        {billing.email}
                        <span className="text-[9px] uppercase tracking-wider text-blue-500">buyer</span>
                      </span>
                    )}
                    {sanitizedUserEmail && sanitizedUserEmail.toLowerCase() !== billing.email.toLowerCase() && (
                      <span data-testid="checkout-recipient-user" className="inline-flex items-center gap-1 text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-sm">
                        {sanitizedUserEmail}
                        <span className="text-[9px] uppercase tracking-wider text-emerald-500">you</span>
                      </span>
                    )}
                    {ccEmails.map((e, i) => (
                      <span key={e} data-testid={`checkout-recipient-cc-${e}`} className="inline-flex items-center gap-1.5 text-[11px] font-semibold bg-slate-100 text-slate-700 border border-slate-200 px-2 py-0.5 rounded-sm">
                        {e}
                        <button
                          data-testid={`checkout-cc-remove-${e}`}
                          onClick={() => removeCcChip(i)}
                          className="text-slate-400 hover:text-red-600"
                          title="Remove"
                        ><X size={10} /></button>
                      </span>
                    ))}
                    {previewRecipients.length === 0 && (
                      <span className="text-[11px] italic text-slate-400">{t("checkout.no_recipients", "No recipients yet — add at least one below.")}</span>
                    )}
                  </div>
                </div>

                <div className="flex gap-1.5">
                  <input
                    data-testid="checkout-cc-input"
                    type="email"
                    placeholder={t("checkout.cc_placeholder", "Add another email (e.g. manager@company.com)")}
                    value={ccInput}
                    onChange={(e) => { setCcInput(e.target.value); if (ccError) setCcError(""); }}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCcChip(); } }}
                    className="flex-1 h-9 text-sm px-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                  <button
                    data-testid="checkout-cc-add"
                    type="button"
                    onClick={addCcChip}
                    disabled={!ccInput.trim()}
                    className="h-9 px-3 text-xs font-bold rounded-sm text-white bg-blue-700 hover:bg-blue-800 disabled:bg-slate-300"
                  >
                    + Add
                  </button>
                </div>
                {ccError && <p data-testid="checkout-cc-error" className="text-[11px] text-red-600 mt-1">{ccError}</p>}
                <p className="text-[11px] text-slate-400 mt-2">
                  {t("checkout.sales_team_auto", "The sales team")} <span className="font-semibold">always</span>{t("checkout.receives_copy", " receives a copy automatically.")}
                </p>
              </div>
            </>
          )}
        </section>

        {/* Right: summary */}
        <aside className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-sm p-5 sticky top-20">
            <h2 className="text-sm font-black uppercase tracking-wider text-slate-700 mb-3">{t("checkout.title", "Order Summary")}</h2>
            <ul className="divide-y divide-slate-100 mb-4 max-h-[320px] overflow-auto">
              {items.map((it) => {
                const q = Number(it.quantity) || 1;
                const disc = Math.max(0, Math.min(1, Number(it.discount) || 0));
                const gross = q * (Number(it.price) || 0);
                const line = gross * (1 - disc);
                const kitLabelMap = { break_in: "Break-In Kit", service_3000: "3,000-hr Kit" };
                return (
                  <li key={it.product_code + "|" + it.currency + "|" + (it.kit_label || "")} data-testid={`checkout-item-${it.product_code}`} className="py-3">
                    <div className="flex items-start gap-2 mb-1.5">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-slate-900 truncate">{it.product_name}</p>
                        <p className="text-[10px] font-mono text-slate-400">{it.product_code}</p>
                        {disc > 0 && (
                          <span className="inline-flex items-center gap-1 mt-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-emerald-100 text-emerald-800 border border-emerald-200">
                            {kitLabelMap[it.kit_label] || "Kit"} · −{Math.round(disc * 100)}%
                          </span>
                        )}
                      </div>
                      <div className="text-right whitespace-nowrap">
                        <p className="text-xs font-mono font-bold text-slate-900">{formatMoney(line, it.currency)}</p>
                        {disc > 0 && (
                          <p className="text-[10px] font-mono text-slate-400 line-through">{formatMoney(gross, it.currency)}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="inline-flex items-center border border-slate-200 rounded-sm">
                        <button
                          data-testid={`checkout-qty-dec-${it.product_code}`}
                          onClick={() => updateQty(it.product_code, it.currency, q - 1)}
                          disabled={q <= 1}
                          className="w-6 h-6 flex items-center justify-center text-slate-500 hover:bg-slate-50 disabled:opacity-30"
                        >
                          <Minus size={10} />
                        </button>
                        <input
                          data-testid={`checkout-qty-input-${it.product_code}`}
                          type="number"
                          min="1"
                          value={q}
                          onChange={(e) => updateQty(it.product_code, it.currency, e.target.value)}
                          className="w-10 h-6 text-[11px] text-center border-x border-slate-200 focus:outline-none focus:bg-blue-50"
                        />
                        <button
                          data-testid={`checkout-qty-inc-${it.product_code}`}
                          onClick={() => updateQty(it.product_code, it.currency, q + 1)}
                          className="w-6 h-6 flex items-center justify-center text-slate-500 hover:bg-slate-50"
                        >
                          <Plus size={10} />
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-mono text-slate-400">@ {formatMoney(it.price, it.currency)}</span>
                        <button
                          data-testid={`checkout-remove-${it.product_code}`}
                          onClick={() => removeItem(it.product_code, it.currency)}
                          className="text-slate-400 hover:text-red-600 transition-colors"
                          title="Remove"
                        >
                          <Trash size={12} />
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
              {items.length === 0 && (
                <li className="py-6 text-center text-xs text-slate-400">{t("cart.empty", "Cart is empty")}</li>
              )}
            </ul>

            <div className="space-y-1.5 text-sm border-t border-slate-200 pt-3">
              <SumRow label={t("cart.subtotal", "Subtotal")} value={formatMoney(subtotal, currency)} />
              {Number(shipping) > 0 && <SumRow label={t("cart.shipping", "Shipping")} value={formatMoney(shipping, currency)} />}
              {Number(salesTax) > 0 && <SumRow label={t("cart.tax", "Sales tax")} value={formatMoney(salesTax, currency)} />}
              {Number(startUp) > 0 && <SumRow label="Start-up" value={formatMoney(startUp, currency)} />}
              <div className="flex items-center justify-between pt-2 border-t border-slate-100 mt-2">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-700">{t("cart.total", "Total")}</span>
                <span data-testid="checkout-total" className="text-lg font-black font-mono text-slate-900">{formatMoney(total, currency)}</span>
              </div>
            </div>

            {error && (
              <div data-testid="checkout-error" className="mt-3 p-2 bg-red-50 border border-red-200 text-xs font-semibold text-red-700 rounded-sm">
                {error}
              </div>
            )}

            <button
              data-testid="checkout-submit"
              disabled={items.length === 0 || submitting}
              onClick={handleSubmit}
              className={`w-full mt-4 h-11 text-sm font-bold rounded-sm text-white transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
                saveAsPreliminary ? "bg-amber-600 hover:bg-amber-700" : "bg-blue-700 hover:bg-blue-800"
              }`}
            >
              {submitting ? <><Spinner size={14} className="animate-spin" /> {saveAsPreliminary ? t("common.saving", "Saving draft…") : t("common.loading", "Creating quote…")}</> :
                saveAsPreliminary ? `${t("checkout.save_preliminary", "Save as Preliminary")} →` :
                isAdmin ? t("checkout.place_order", "Create Quote") : t("checkout.place_order", "Submit & Get a Quote")}
            </button>

            {!user && (
              <p className="text-[11px] text-slate-400 mt-2 text-center">
                {t("checkout.no_account_needed", "No account needed — a specialist will follow up with a formal quote via email.")}
              </p>
            )}
          </div>
        </aside>
      </main>
    </div>
  );
}

function BillingForm({ billing, setBilling, testPrefix }) {
  const t = useT();
  const upd = (k) => (e) => setBilling({ ...billing, [k]: e.target.value });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <Field label={t("checkout.company_name", "Company Name")} value={billing.company_name} onChange={upd("company_name")} testid={`${testPrefix}-company`} />
      <Field label={t("checkout.contact_name", "Contact Name")} value={billing.contact_name} onChange={upd("contact_name")} testid={`${testPrefix}-contact`} />
      <Field label="Email" type="email" value={billing.email} onChange={upd("email")} testid={`${testPrefix}-email`} />
      <Field label="Phone" type="tel" value={billing.phone} onChange={upd("phone")} testid={`${testPrefix}-phone`} />
      <div className="md:col-span-2">
        <label className="block text-xs font-semibold text-slate-600 mb-1">{t("checkout.address_label", "Billing / Ship-to Address")}</label>
        <textarea
          data-testid={`${testPrefix}-address`}
          rows={2}
          value={billing.address}
          onChange={upd("address")}
          placeholder={t("checkout.address_placeholder", "Street, City, State/Province, Postal Code, Country")}
          className="w-full text-sm border border-slate-300 rounded-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600"
        />
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text" }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={onChange}
        className="w-full h-10 px-3 text-sm border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
      />
    </div>
  );
}

function NumInput({ label, value, onChange, tid }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold text-slate-600 mb-1">{label}</label>
      <input
        data-testid={tid}
        type="number"
        step="0.01"
        min="0"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-9 px-2 text-sm font-mono text-right border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
      />
    </div>
  );
}

function Row({ label, value, mono, bold }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-slate-500">{label}</span>
      <span className={`${mono ? "font-mono" : ""} ${bold ? "font-bold text-slate-900" : "text-slate-700"}`}>{value}</span>
    </div>
  );
}

function SuccessField({ label, value, mono }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">{label}</p>
      <p className={`text-sm text-slate-800 ${mono ? "font-mono" : ""} break-words`}>{value || "—"}</p>
    </div>
  );
}

function SumRow({ label, value }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-slate-500">{label}</span>
      <span className="font-mono text-slate-700">{value}</span>
    </div>
  );
}

/**
 * CustomerTierBanner — admin-facing visibility of what Customer_Aux
 * discount + payment term will auto-apply to the new quote. Only shows
 * when a tier exists (customer has a real Customer_Aux row). When no tier
 * is on file, a muted line makes the absence visible.
 *
 * Never surfaces to non-admins (the parent's `isAdmin` gate controls the
 * fetch effect — tier stays null for customers/guests).
 */
function CustomerTierBanner({ tier, t }) {
  if (!tier) return null;
  if (!tier.has_tier) {
    return (
      <div data-testid="checkout-tier-banner-none"
        className="mt-2 p-2 border border-dashed border-slate-200 bg-slate-50 rounded-sm">
        <p className="text-[11px] text-slate-500">
          {t("checkout.tier_none", "No Customer_Aux discount tier on file — full retail. (Ask a sales + accounting admin pair to propose a tier from the Discounts admin page.)")}
        </p>
      </div>
    );
  }
  const parts = [];
  if (tier.d_series_pct_off > 0) parts.push(`D-series ${tier.d_series_pct_off}% OFF`);
  if (tier.spare_pct_off > 0) parts.push(`Spare ${tier.spare_pct_off}% OFF`);
  if (tier.b_series_pct_off > 0) parts.push(`B-series ${tier.b_series_pct_off}% OFF`);
  const term = tier.payment_term_days > 0 ? `Net-${tier.payment_term_days}` : "Net-30";
  return (
    <div data-testid="checkout-tier-banner"
      className="mt-2 p-3 border-l-4 border-violet-500 bg-violet-50 rounded-sm">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-[10px] font-bold uppercase tracking-wider text-violet-900 bg-violet-100 px-2 py-0.5 rounded">
          {t("checkout.tier_auto_applied", "Tier auto-applied")}
        </span>
        <span className="text-[11px] text-slate-500">
          {t("checkout.tier_explainer", "From Customer_Aux. Manual line discounts (e.g. Starter-Kit bundles) will keep their own rate.")}
        </span>
      </div>
      <div className="flex flex-wrap gap-3 text-sm font-semibold text-violet-900">
        {parts.length === 0 ? (
          <span className="text-slate-500 italic">
            {t("checkout.tier_zero_discount", "No discount multipliers set (on-file tier has 0% everywhere)")}
          </span>
        ) : parts.map(p => (
          <span key={p} data-testid={`checkout-tier-${p.split(" ")[0].toLowerCase()}`}>{p}</span>
        ))}
        <span className="text-slate-400">·</span>
        <span data-testid="checkout-tier-term">{term}</span>
      </div>
    </div>
  );
}
